import { useEffect, useState } from "react";
import {
  Alan,
  BosDurum,
  Buton,
  Ikon,
  Iskelet,
  SatirDugmesi,
  YardimGovdesi,
  cn,
  yardimKategoriIkonu,
} from "@koclukpanel/ui";
import {
  ApiHatasi,
  yardimAra,
  yardimBolumu,
  yardimYazisi,
  type YardimBolumu,
  type YardimYazisi,
} from "./api";

/**
 * NASIL KULLANILIR — öğrenci ve veli için yardım yazıları (aşama 1, 05.10.2026)
 *
 * ================================================================
 * NE GÖSTERİLECEĞİNE SUNUCU KARAR VERİYOR
 * ================================================================
 * Panelle aynı uçlar (`yardim/icerik`). Öğrenci öğrenci yazılarını,
 * veli veli yazılarını alır; ayrım rol adıyla değil izin ve yüzeyle
 * sunucuda yapılıyor (`yardim-gorunurluk.ts`). Bu ekran hiçbir şey
 * süzmüyor: süzseydi, aynı uca doğrudan istek atan biri her şeyi görürdü.
 *
 * ================================================================
 * ORTAK EKRAN, "SİZ" DİLİ
 * ================================================================
 * Ekran öğrenci kabuğunda (Hesabım) ve veli kabuğunda aynen açılıyor;
 * kimin göreceği belli olmadığı için ortak metinler "siz" der
 * (`VeliKabugu.tsx` dil kuralı). Yazıların kendi dili sunucudan gelir.
 *
 * ================================================================
 * MOBİL ÖNCELİKLİ: LİSTE → YAZI, AKORDEON DEĞİL
 * ================================================================
 * Panelde yazı yerinde açılıyor. Telefonda uzun bir yazıyı listenin
 * ortasında açmak, kullanıcıyı listede kaybettiriyor; yazı tam ekran
 * açılıyor ve "← Tüm yazılar" ile listeye dönülüyor (Hesabım'daki geri
 * düğmesi deseni).
 */

/** Aramanın başlaması için en az karakter — panelle aynı eşik. */
const ARAMA_EN_AZ = 2;

export function NasilKullanilir() {
  const [bolum, setBolum] = useState<YardimBolumu | null>(null);
  const [hata, setHata] = useState<string | null>(null);
  const [deneme, setDeneme] = useState(0);
  const [sorgu, setSorgu] = useState("");
  const [sonuclar, setSonuclar] = useState<YardimYazisi[] | null>(null);
  const [aramaHatasi, setAramaHatasi] = useState(false);
  const [acik, setAcik] = useState<YardimYazisi | null>(null);
  /** Kategori kartından seçilen süzgeç (UI/UX Dalga 2, M10). */
  const [kategori, setKategori] = useState<string | null>(null);

  useEffect(() => {
    let gecerli = true;
    setHata(null);
    void yardimBolumu().then(
      (b) => {
        if (gecerli) setBolum(b);
      },
      (err: unknown) => {
        if (gecerli) setHata(err instanceof ApiHatasi ? err.message : "Bağlantınızı kontrol edin.");
      },
    );
    return () => {
      gecerli = false;
    };
  }, [deneme]);

  const araniyor = sorgu.trim().length >= ARAMA_EN_AZ;
  const tumYazilar = bolum ? bolum.kategoriler.flatMap((k) => k.yazilar) : [];
  const baslangic = bolum?.kategoriler.find((k) => k.ad === "İlk gün")?.yazilar ?? [];

  useEffect(() => {
    if (!araniyor) {
      setSonuclar(null);
      setAramaHatasi(false);
      return;
    }
    /*
      ESKİ CEVAP YENİSİNİN ÜSTÜNE YAZILMAZ. Her tuşta istek gidiyor;
      "şi" için gelen geç bir yanıt, "şifre" için gelen yanıtı ezseydi
      ekran yazılan kelimeyle çelişirdi.
    */
    let gecerli = true;
    setAramaHatasi(false);
    void yardimAra(sorgu.trim()).then(
      (s) => {
        if (gecerli) setSonuclar(s);
      },
      () => {
        if (gecerli) setAramaHatasi(true);
      },
    );
    return () => {
      gecerli = false;
    };
  }, [sorgu, araniyor]);

  function yaziAc(y: YardimYazisi): void {
    setAcik(y);
    /*
      Yazı listeden geldiği gövdeyle HEMEN açılıyor; tek yazı ucu ayrıca
      çağrılıyor çünkü okunma sayacını o artırıyor (F-HLP-13) ve gövdenin
      en güncel hâlini veriyor. Düşerse yazı yine okunur — sayaç bir
      kolaylık, okumayı engellemesi gerekmez.
    */
    void yardimYazisi(y.slug).then(
      (taze) => setAcik((simdiki) => (simdiki?.slug === taze.slug ? taze : simdiki)),
      () => undefined,
    );
  }

  if (acik) {
    return (
      <article className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <Buton gorunum="sessiz" onClick={() => setAcik(null)}>
            ← Tüm yazılar
          </Buton>
        </div>
        <p className="text-notr-600 text-xs">{acik.kategori}</p>
        <h2 className="text-notr-900 text-lg font-semibold">{acik.baslik}</h2>
        {/*
          Gövde `YardimGovdesi` ile çiziliyor (UI/UX Dalga 2, M10): adım listesi,
          "Sık karşılaşılanlar" kutusu, tıklanabilir ilgili yazılar. Markdown
          çözülmüyor (panel `Yardim.tsx` ile aynı karar); düz metin paragraf
          olarak aynen çıkıyor.
        */}
        <YardimGovdesi
          govde={acik.govde}
          onIlgili={(baslik) => {
            const hedef = tumYazilar.find((y) => y.baslik === baslik);
            if (hedef) yaziAc(hedef);
          }}
          ilgiliBulunur={(baslik) => tumYazilar.some((y) => y.baslik === baslik)}
        />
      </article>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4 p-4">
      <h1 className="text-notr-900 text-lg font-semibold">Nasıl kullanılır</h1>

      <Alan
        etiket="Yardımda ara"
        type="search"
        value={sorgu}
        onChange={(e) => setSorgu(e.target.value)}
        placeholder="“şifre”, “deneme”, “görev”"
      />

      {araniyor ? (
        <AramaSonucu sonuclar={sonuclar} hatali={aramaHatasi} onAc={yaziAc} />
      ) : hata ? (
        <BosDurum
          ton="hata"
          baslik="Yardım yazıları alınamadı"
          aciklama={hata}
          eylem={
            <Buton gorunum="ikincil" onClick={() => setDeneme((n) => n + 1)}>
              Tekrar dene
            </Buton>
          }
        />
      ) : bolum === null ? (
        <Iskelet satir={4} />
      ) : bolum.toplam === 0 ? (
        <BosDurum
          baslik="Size görünen yardım yazısı yok"
          aciklama="Yazılar hesabınıza göre seçilir; bu, sistemde içerik olmadığı anlamına gelmez."
        />
      ) : (
        <>
          {baslangic.length > 0 && kategori === null && (
            <section aria-label="Buradan başla" className="flex flex-col gap-2">
              <h2 className="text-notr-800 text-sm font-semibold">Buradan başla</h2>
              {baslangic.slice(0, 3).map((y) => (
                <button
                  key={y.slug}
                  type="button"
                  onClick={() => yaziAc(y)}
                  className="border-marka-200 bg-marka-50 rounded-kart min-h-dokunma flex items-center justify-between gap-3 border p-3.5 text-left"
                >
                  <span className="text-marka-700 text-sm font-semibold">{y.baslik}</span>
                  <span aria-hidden="true" className="text-marka-700">
                    →
                  </span>
                </button>
              ))}
            </section>
          )}

          {bolum.kategoriler.length > 1 && (
            <section aria-label="Konular" className="flex flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <h2 className="text-notr-800 text-sm font-semibold">Konular</h2>
                {kategori !== null && (
                  <Buton gorunum="sessiz" onClick={() => setKategori(null)}>
                    ← Tüm konular
                  </Buton>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2">
                {bolum.kategoriler.map((k) => (
                  <button
                    key={k.ad}
                    type="button"
                    aria-pressed={kategori === k.ad}
                    onClick={() => setKategori(kategori === k.ad ? null : k.ad)}
                    className={cn(
                      "rounded-kart min-h-dokunma flex items-center gap-2.5 border p-3 text-left",
                      kategori === k.ad
                        ? "border-marka-600 bg-marka-50"
                        : "border-notr-200 bg-notr-0",
                    )}
                  >
                    <span className="bg-marka-50 text-marka-700 grid size-9 shrink-0 place-items-center rounded-[10px]">
                      <Ikon ad={yardimKategoriIkonu(k.ad)} />
                    </span>
                    <span className="min-w-0">
                      <span className="text-notr-900 block text-[13px] leading-tight font-semibold">
                        {k.ad}
                      </span>
                      <span className="text-notr-600 block text-xs">{k.yazilar.length} yazı</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>
          )}

          {bolum.kategoriler
            .filter((k) => kategori === null || k.ad === kategori)
            .map((k) => (
              <section key={k.ad} className="flex flex-col gap-2" aria-label={k.ad}>
                <h2 className="text-notr-800 text-sm font-semibold">{k.ad}</h2>
                <YaziListesi yazilar={k.yazilar} onAc={yaziAc} />
              </section>
            ))}
        </>
      )}
    </div>
  );
}

function AramaSonucu({
  sonuclar,
  hatali,
  onAc,
}: {
  sonuclar: YardimYazisi[] | null;
  hatali: boolean;
  onAc: (y: YardimYazisi) => void;
}) {
  if (hatali) {
    return (
      <p role="alert" className="text-hata-700 text-sm">
        Arama yapılamadı. Bağlantınızı kontrol edip yeniden deneyin.
      </p>
    );
  }
  if (sonuclar === null) return <p className="text-notr-600 text-sm">Aranıyor…</p>;
  if (sonuclar.length === 0) {
    /*
      "Bulunamadı" ile "size kapalı" AYIRT EDİLMİYOR (panelle aynı karar):
      ayırt etseydik, olmayan bir sonucun aslında gizlendiğini söylemiş
      olurduk.
    */
    return (
      <p className="text-notr-600 text-sm">
        Aramanıza uyan yazı yok. Başka bir kelime deneyin ya da sorunuzu Mesajlar ekranından yazın.
      </p>
    );
  }
  return (
    <section className="flex flex-col gap-2" aria-label="Arama sonuçları">
      <h2 className="text-notr-800 text-sm font-semibold">Arama sonuçları ({sonuclar.length})</h2>
      <YaziListesi yazilar={sonuclar} onAc={onAc} />
    </section>
  );
}

function YaziListesi({
  yazilar,
  onAc,
}: {
  yazilar: readonly YardimYazisi[];
  onAc: (y: YardimYazisi) => void;
}) {
  return (
    <ul className="flex flex-col gap-2">
      {yazilar.map((y) => (
        <li key={y.slug}>
          <SatirDugmesi onClick={() => onAc(y)}>
            {/*
              `SatirMetni` DEĞİL: o başlığı `truncate` ile tek satıra kesiyor
              ve 390px'te uzun yazı başlıkları üç noktaya düşerdi
              (Hesabim.tsx'teki ölçülmüş not). Başlık burada satırın tek
              içeriği; kırılarak sığması kesilmesinden iyidir.
            */}
            <span className="text-notr-900 min-w-0 flex-1 text-sm font-medium">{y.baslik}</span>
            <span aria-hidden="true" className="text-notr-400 shrink-0">
              ›
            </span>
          </SatirDugmesi>
        </li>
      ))}
    </ul>
  );
}
